import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'
import { calculateAllMetrics } from '../utils/financialCalculations'
import {
  useDateFormatter,
  formatMonthYear as formatMonthYearUtil,
  formatShortMonth as formatShortMonthUtil
} from '../utils/dateFormatter'
import KPICard from './KPICard'
import HealthScoreCard from './HealthScoreCard'
import InsightsPanel from './InsightsPanel'
import AutomationsCard from './AutomationsCard'
import ReportsModal from './ReportsModal'
import DashboardOverviewHeader from './DashboardOverviewHeader'
import CustomerAcquisitionModal from './CustomerAcquisitionModal'
import CustomerAcquisitionInfoModal from './CustomerAcquisitionInfoModal'
import { ArrowDownToLine, BarChart3, X as XIcon } from 'lucide-react'
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts'

// Keep report visuals aligned with the app's emerald/green palette.
const COLORS = ['#10B981', '#34D399', '#6EE7B7', '#059669', '#A7F3D0', '#047857']

// Expense Breakdown pie chart palette (Operating / Marketing / Technology)
const EXPENSE_BREAKDOWN_COLORS = {
  operating: '#FF1B54',
  marketing: '#F58AA4',
  technology: '#B92F59'
}

const RADIAN = Math.PI / 180

const DASHBOARD_QUERY_TIMEOUT_MS = 12000

const withTimeout = async (promise, timeoutMs, timeoutMessage) => {
  let timeoutId

  try {
    return await Promise.race([
      promise,
      new Promise((_, reject) => {
        timeoutId = setTimeout(() => reject(new Error(timeoutMessage)), timeoutMs)
      }),
    ])
  } finally {
    if (timeoutId) clearTimeout(timeoutId)
  }
}

const isImmediatePreviousMonth = (currentMetric, candidatePreviousMetric) => {
  if (!currentMetric || !candidatePreviousMetric) return false

  const currentMonthIndex = (Number(currentMetric.year) * 12) + (Number(currentMetric.month) - 1)
  const previousMonthIndex = (Number(candidatePreviousMetric.year) * 12) + (Number(candidatePreviousMetric.month) - 1)

  return currentMonthIndex - previousMonthIndex === 1
}

const getMetricMonthKey = (metric) => {
  if (!metric) return ''
  return `${metric.year}-${String(metric.month).padStart(2, '0')}`
}

const getMonthIndex = (year, month) => (Number(year) * 12) + Number(month) - 1

const buildMonthKeys = (anchorDate) => {
  return Array.from({ length: 6 }, (_, index) => {
    const date = new Date(anchorDate.getFullYear(), anchorDate.getMonth() - 5 + index, 1)
    return getMetricMonthKey({ year: date.getFullYear(), month: date.getMonth() + 1 })
  })
}

const FinancialDashboard = ({ reportsOpen = false, onCloseReports, onDeleteData }) => {
  const { user } = useAuth()
  const { t, i18n } = useTranslation('dashboard')
  const { currentLanguage } = useDateFormatter()
  const [historicalData, setHistoricalData] = useState([])
  const [orderedMonthlyMetrics, setOrderedMonthlyMetrics] = useState([])
  const [selectedComparisonMonthKey, setSelectedComparisonMonthKey] = useState(null)
  const [selectedDashboardMonthKey, setSelectedDashboardMonthKey] = useState('')
  const [monthWindowAnchor] = useState(() => new Date())
  const [businessName, setBusinessName] = useState('')
  const [businessId, setBusinessId] = useState(null)
  const [hasAnyMonthlyMetrics, setHasAnyMonthlyMetrics] = useState(null)
  const [automationProfile, setAutomationProfile] = useState(null)
  const [hasConfiguredAutomation, setHasConfiguredAutomation] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [lastDashboardUpdateAt, setLastDashboardUpdateAt] = useState(null)
  const [dashboardClock, setDashboardClock] = useState(() => Date.now())
  const [activeReportIndex, setActiveReportIndex] = useState(0)

  const [deleteMonthOpen, setDeleteMonthOpen] = useState(false)
  const [selectedDeleteRow, setSelectedDeleteRow] = useState(null)
  const [deleteMonthError, setDeleteMonthError] = useState('')
  const [isDeletingMonth, setIsDeletingMonth] = useState(false)
  const [customerAcquisitionModalOpen, setCustomerAcquisitionModalOpen] = useState(false)
  const [customerAcquisitionInfoModalOpen, setCustomerAcquisitionInfoModalOpen] = useState(false)
  const [isSavingCustomerAcquisition, setIsSavingCustomerAcquisition] = useState(false)
  const [customerAcquisitionSaveError, setCustomerAcquisitionSaveError] = useState('')
  const fetchRequestIdRef = useRef(0)
  const hasInitializedDashboardMonthRef = useRef(false)
  const userEmail = user?.email || ''
  const noDataMessage = useMemo(
    () => i18n.getFixedT(currentLanguage, 'dashboard')('charts.noDataMessage'),
    [currentLanguage, i18n]
  )

  const getMonthName = useCallback(
    (monthNumber, year) => {
      return formatMonthYearUtil(monthNumber, year, currentLanguage)
    },
    [currentLanguage]
  )

  const getShortMonthName = useCallback(
    (monthNumber) => {
      return formatShortMonthUtil(monthNumber, currentLanguage)
    },
    [currentLanguage]
  )

  const processHistoricalData = useCallback(
    (metrics) => {
      return metrics
        .map((m) => {
          const calculated = calculateAllMetrics(m)
          return {
            id: m.id,
            monthNumber: m.month,
            year: m.year,
            month: getMonthName(m.month, m.year),
            shortMonth: getShortMonthName(m.month),
            chartMonth: `${getShortMonthName(m.month)} '${String(m.year).slice(-2)}`,
            revenue: m.revenue || 0,
            expenses: m.expenses || 0,
            leads: m.leads || 0,
            customers: m.customers || 0,
            netProfit: calculated.netProfit,
            profitMargin: calculated.profitMargin,
            conversionRate: calculated.conversionRate,
            expenseRatio: calculated.expenseRatio,
            date: new Date(m.year, m.month - 1, 1)
          }
        })
        .sort((a, b) => a.date - b.date)
    },
    [getMonthName, getShortMonthName]
  )

  const fetchAutomationProfile = useCallback(async (bizId) => {
    setHasConfiguredAutomation(false)
    if (!bizId) {
      setAutomationProfile(null)
      return
    }

    try {
      const [profileResult, automationResult] = await Promise.all([
        supabase
          .from('business_automation_profiles')
          .select('business_id, is_enabled, enabled_at, enabled_by, notes')
          .eq('business_id', bizId)
          .maybeSingle(),
        supabase.rpc('has_active_customer_automation'),
      ])

      const { data, error } = profileResult
      const { data: hasActiveAutomation, error: automationError } = automationResult

      if (error) throw error

      const resolvedProfile = data || {
        business_id: bizId,
        is_enabled: false,
        enabled_at: null,
        enabled_by: null,
        notes: null,
      }
      setAutomationProfile(resolvedProfile)
      if (automationError) {
        console.error('Configured automation status fetch error:', automationError)
        setHasConfiguredAutomation(false)
      } else {
        setHasConfiguredAutomation(Boolean(hasActiveAutomation))
      }
    } catch (err) {
      console.error('Automation profile fetch error:', err)
      // Fail closed (hide the card) if profile cannot be fetched.
      setHasConfiguredAutomation(false)
      setAutomationProfile((prev) =>
        prev || {
          business_id: bizId,
          is_enabled: false,
          enabled_at: null,
          enabled_by: null,
          notes: null,
        }
      )
    }
  }, [])

  const fetchDashboardData = useCallback(async () => {
    const requestId = fetchRequestIdRef.current + 1
    fetchRequestIdRef.current = requestId
    const isStaleRequest = () => requestId !== fetchRequestIdRef.current

    if (!userEmail) {
      if (!isStaleRequest()) setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError('')
      setHasAnyMonthlyMetrics(null)

      // Get user's business
      const { data: business, error: businessError } = await withTimeout(
        supabase
          .from('businesses')
          .select('id, business_name')
          .eq('owner_email', userEmail)
          .single(),
        DASHBOARD_QUERY_TIMEOUT_MS,
        'Timed out while loading business data'
      )

      if (isStaleRequest()) return

      if (businessError) {
        console.log('No business found for user')
        setBusinessName('')
        setBusinessId(null)
        setAutomationProfile(null)
        setHasConfiguredAutomation(false)
        setHistoricalData([])
        setOrderedMonthlyMetrics([])
        setSelectedComparisonMonthKey(null)
        return
      }

      setBusinessName(business?.business_name || '')
      setBusinessId(business?.id || null)
      void fetchAutomationProfile(business?.id)

      // Get last 12 months of data
      const currentDate = new Date()
      const startDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - 11, 1)

      const { data: metrics, error: metricsError } = await withTimeout(
        supabase
          .from('monthly_metrics')
          .select('*')
          .eq('business_id', business.id)
          .gte('created_at', startDate.toISOString())
          .order('year', { ascending: true })
          .order('month', { ascending: true }),
        DASHBOARD_QUERY_TIMEOUT_MS,
        'Timed out while loading monthly metrics'
      )

      if (isStaleRequest()) return

      if (metricsError) throw metricsError
      setHasAnyMonthlyMetrics((metrics?.length || 0) > 0)

      // Process the data
      const processedData = processHistoricalData(metrics || [])
      setHistoricalData(processedData)

      // Keep ordered monthly rows so KPI comparison baseline can be selected dynamically.
      const orderedMetrics = [...(metrics || [])].sort((a, b) => {
        if (a.year !== b.year) return a.year - b.year
        return a.month - b.month
      })
      setOrderedMonthlyMetrics(orderedMetrics)

      if ((metrics?.length || 0) === 0) {
        try {
          const { data: anyHistoricalRows, error: anyHistoricalError } = await withTimeout(
            supabase
              .from('monthly_metrics')
              .select('id')
              .eq('business_id', business.id)
              .limit(1),
            DASHBOARD_QUERY_TIMEOUT_MS,
            'Timed out while checking historical monthly metrics'
          )

          if (isStaleRequest()) return

          if (anyHistoricalError) throw anyHistoricalError
          setHasAnyMonthlyMetrics((anyHistoricalRows?.length || 0) > 0)
        } catch (metricsPresenceError) {
          if (isStaleRequest()) return
          setHasAnyMonthlyMetrics(null)
          console.error('Historical metrics presence check failed:', metricsPresenceError)
        }
      }

      setLastDashboardUpdateAt(Date.now())
    } catch (err) {
      if (isStaleRequest()) return
      setHasAnyMonthlyMetrics(null)
      setOrderedMonthlyMetrics([])
      setSelectedComparisonMonthKey(null)
      setError(noDataMessage)
      console.error('Dashboard error:', err)
    } finally {
      if (!isStaleRequest()) {
        setLoading(false)
      }
    }
  }, [fetchAutomationProfile, noDataMessage, processHistoricalData, userEmail])

  useEffect(() => {
    fetchDashboardData()
  }, [fetchDashboardData])

  useEffect(() => {
    const intervalId = setInterval(() => setDashboardClock(Date.now()), 60000)
    return () => clearInterval(intervalId)
  }, [])

  useEffect(() => {
    if (!businessId) return

    const channel = supabase
      .channel(`business-automation-profile-${businessId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'business_automation_profiles',
          filter: `business_id=eq.${businessId}`,
        },
        (payload) => {
          if (payload.eventType === 'DELETE') {
            setAutomationProfile((prev) => ({
              ...(prev || { business_id: businessId }),
              is_enabled: false,
              enabled_at: null,
              enabled_by: null,
            }))
            return
          }

          if (!payload.new) return

          setAutomationProfile((prev) => ({
            ...(prev || {}),
            ...payload.new,
          }))
        }
      )

    channel.subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [businessId])

  useEffect(() => {
    if (reportsOpen) setActiveReportIndex(0)
  }, [reportsOpen])

  useEffect(() => {
    if (!deleteMonthOpen) return

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (isDeletingMonth) return
        setDeleteMonthOpen(false)
        setSelectedDeleteRow(null)
        setDeleteMonthError('')
      }
    }

    document.addEventListener('keydown', onKeyDown)

    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.body.style.overflow = prevOverflow
    }
  }, [deleteMonthOpen, isDeletingMonth])

  const handleOpenDeleteMonth = (row) => {
    if (isDeletingMonth) return
    setSelectedDeleteRow(row)
    setDeleteMonthError('')
    setDeleteMonthOpen(true)
  }

  const handleCancelDeleteMonth = () => {
    if (isDeletingMonth) return
    setDeleteMonthOpen(false)
    setSelectedDeleteRow(null)
    setDeleteMonthError('')
  }

  const handleConfirmDeleteMonth = async () => {
    if (!selectedDeleteRow || isDeletingMonth) return

    setIsDeletingMonth(true)
    setDeleteMonthError('')

    try {
      const { error: deleteError } = await supabase.rpc('delete_business_metrics_for_month', {
        p_month: selectedDeleteRow.monthNumber,
        p_year: selectedDeleteRow.year,
      })

      if (deleteError) throw deleteError

      setDeleteMonthOpen(false)
      setSelectedDeleteRow(null)
      await fetchDashboardData()
    } catch (err) {
      console.error('Error deleting month metrics:', err)
      setDeleteMonthError(t('charts.deleteMonthError'))
    } finally {
      setIsDeletingMonth(false)
    }
  }

  const dashboardMonthOptions = useMemo(
    () => buildMonthKeys(monthWindowAnchor).map((key) => {
      const [year, month] = key.split('-').map(Number)
      return { key, label: getMonthName(month, year) }
    }),
    [getMonthName, monthWindowAnchor]
  )

  const currentMonthKey = useMemo(
    () => getMetricMonthKey({ year: monthWindowAnchor.getFullYear(), month: monthWindowAnchor.getMonth() + 1 }),
    [monthWindowAnchor]
  )

  const acquisitionMonthKey = selectedDashboardMonthKey || currentMonthKey
  const [acquisitionYear, acquisitionMonth] = acquisitionMonthKey.split('-').map(Number)
  const acquisitionMonthLabel = getMonthName(acquisitionMonth, acquisitionYear)

  const handleSaveCustomerAcquisition = useCallback(async ({ leads, customers }) => {
    if (isSavingCustomerAcquisition) return

    setIsSavingCustomerAcquisition(true)
    setCustomerAcquisitionSaveError('')
    const [year, month] = (selectedDashboardMonthKey || currentMonthKey).split('-').map(Number)

    try {
      const { error: saveError } = await supabase.rpc('add_customer_acquisition_counts', {
        p_month: month,
        p_year: year,
        p_leads: leads,
        p_customers: customers,
      })

      if (saveError) throw saveError

      setCustomerAcquisitionModalOpen(false)
      await fetchDashboardData()
    } catch (saveError) {
      console.error('Error saving customer acquisition counts:', saveError)
      setCustomerAcquisitionSaveError(t('customerAcquisitionModal.saveError'))
    } finally {
      setIsSavingCustomerAcquisition(false)
    }
  }, [currentMonthKey, fetchDashboardData, isSavingCustomerAcquisition, selectedDashboardMonthKey, t])

  const handleCloseCustomerAcquisitionModal = useCallback(() => {
    if (isSavingCustomerAcquisition) return
    setCustomerAcquisitionModalOpen(false)
    setCustomerAcquisitionSaveError('')
  }, [isSavingCustomerAcquisition])

  const handleCloseCustomerAcquisitionInfoModal = useCallback(() => {
    setCustomerAcquisitionInfoModalOpen(false)
  }, [])

  useEffect(() => {
    if (loading) return

    const selectedOptionExists = dashboardMonthOptions.some(
      (option) => option.key === selectedDashboardMonthKey
    )

    if (!hasInitializedDashboardMonthRef.current || !selectedOptionExists) {
      const latestMetric = orderedMonthlyMetrics[orderedMonthlyMetrics.length - 1]
      const latestKey = getMetricMonthKey(latestMetric)
      const fallbackKey = dashboardMonthOptions.some((option) => option.key === latestKey)
        ? latestKey
        : currentMonthKey

      hasInitializedDashboardMonthRef.current = true
      setSelectedDashboardMonthKey(fallbackKey)
    }
  }, [currentMonthKey, dashboardMonthOptions, loading, orderedMonthlyMetrics, selectedDashboardMonthKey])

  const selectedDashboardMonthIndex = useMemo(() => {
    if (!selectedDashboardMonthKey) return getMonthIndex(monthWindowAnchor.getFullYear(), monthWindowAnchor.getMonth() + 1)
    const [year, month] = selectedDashboardMonthKey.split('-').map(Number)
    return getMonthIndex(year, month)
  }, [monthWindowAnchor, selectedDashboardMonthKey])

  const historicalDataThroughSelectedMonth = useMemo(
    () => historicalData.filter((row) => getMonthIndex(row.year, row.monthNumber) <= selectedDashboardMonthIndex),
    [historicalData, selectedDashboardMonthIndex]
  )

  const currentRawMetrics = useMemo(() => {
    if (!selectedDashboardMonthKey) return null
    return orderedMonthlyMetrics.find((metric) => getMetricMonthKey(metric) === selectedDashboardMonthKey) || null
  }, [orderedMonthlyMetrics, selectedDashboardMonthKey])

  const comparisonMonthOptions = useMemo(() => {
    return orderedMonthlyMetrics
      .filter((metric) => getMonthIndex(metric.year, metric.month) < selectedDashboardMonthIndex)
      .map((metric) => ({
        key: getMetricMonthKey(metric),
        label: getMonthName(metric.month, metric.year),
        rawData: metric
      }))
      .reverse()
  }, [getMonthName, orderedMonthlyMetrics, selectedDashboardMonthIndex])

  useEffect(() => {
    if (comparisonMonthOptions.length === 0) {
      if (selectedComparisonMonthKey !== null) {
        setSelectedComparisonMonthKey(null)
      }
      return
    }

    const selectedStillExists = comparisonMonthOptions.some(
      (option) => option.key === selectedComparisonMonthKey
    )

    if (!selectedStillExists) {
      setSelectedComparisonMonthKey(comparisonMonthOptions[0].key)
    }
  }, [comparisonMonthOptions, selectedComparisonMonthKey])

  const selectedComparisonOption = useMemo(() => {
    if (comparisonMonthOptions.length === 0) return null

    return (
      comparisonMonthOptions.find((option) => option.key === selectedComparisonMonthKey) ||
      comparisonMonthOptions[0]
    )
  }, [comparisonMonthOptions, selectedComparisonMonthKey])

  const previousRawMetrics = selectedComparisonOption?.rawData || null

  const currentMetrics = useMemo(() => {
    if (!currentRawMetrics) return null

    const current = calculateAllMetrics(currentRawMetrics, previousRawMetrics)
    return { ...current, rawData: currentRawMetrics }
  }, [currentRawMetrics, previousRawMetrics])

  const previousMetrics = useMemo(() => {
    if (!previousRawMetrics) return null

    const previous = calculateAllMetrics(previousRawMetrics)
    return { ...previous, rawData: previousRawMetrics }
  }, [previousRawMetrics])

  const hasComparisonMonthSelector = comparisonMonthOptions.length > 1

  const hasRevenueComparison =
    previousMetrics?.rawData?.revenue !== null &&
    previousMetrics?.rawData?.revenue !== undefined

  const comparisonTrendLabel = useMemo(() => {
    if (!currentRawMetrics || !previousRawMetrics) return null

    if (isImmediatePreviousMonth(currentRawMetrics, previousRawMetrics)) {
      return null
    }

    const selectedMonthLabel =
      selectedComparisonOption?.label || getMonthName(previousRawMetrics.month, previousRawMetrics.year)

    return t('kpi.comparison.vsMonth', { month: selectedMonthLabel })
  }, [
    currentRawMetrics,
    getMonthName,
    previousRawMetrics,
    selectedComparisonOption,
    t
  ])

  const comparisonOverviewSubtitle = useMemo(() => {
    if (!currentRawMetrics || !previousRawMetrics || isImmediatePreviousMonth(currentRawMetrics, previousRawMetrics)) {
      return t('kpi.overviewSubtitle')
    }

    const monthLabel =
      selectedComparisonOption?.label || getMonthName(previousRawMetrics.month, previousRawMetrics.year)

    return t('kpi.overviewSubtitleForMonth', { month: monthLabel })
  }, [currentRawMetrics, getMonthName, previousRawMetrics, selectedComparisonOption, t])

  const rawLeadsInput = currentMetrics?.rawData?.leads
  const hasUsableLeadsInput =
    rawLeadsInput !== null &&
    rawLeadsInput !== undefined &&
    !(typeof rawLeadsInput === 'string' && rawLeadsInput.trim() === '') &&
    Number.isFinite(Number(rawLeadsInput)) &&
    Number(rawLeadsInput) > 0

  const visibleKpiCount =
    4 + (hasUsableLeadsInput ? 1 : 0) + (hasRevenueComparison ? 1 : 0)

  const updatedAgoLabel = useMemo(() => {
    if (!lastDashboardUpdateAt) return ''

    const elapsedSeconds = Math.max(0, Math.floor((dashboardClock - lastDashboardUpdateAt) / 1000))
    if (elapsedSeconds < 60) return t('kpi.updatedJustNow')

    const elapsedMinutes = Math.floor(elapsedSeconds / 60)
    if (elapsedMinutes < 60) return t('kpi.updatedMinutesAgo', { count: elapsedMinutes })

    const elapsedHours = Math.floor(elapsedMinutes / 60)
    if (elapsedHours < 24) return t('kpi.updatedHoursAgo', { count: elapsedHours })

    const elapsedDays = Math.floor(elapsedHours / 24)
    return t(elapsedDays === 1 ? 'kpi.updatedDayAgo' : 'kpi.updatedDaysAgo', { count: elapsedDays })
  }, [dashboardClock, lastDashboardUpdateAt, t])

  const kpiGridXlClass =
    visibleKpiCount === 6 ? 'xl:grid-cols-6' :
    visibleKpiCount === 5 ? 'xl:grid-cols-5' :
    'xl:grid-cols-4'

  // Custom tooltip for Gross Profit Margin Trend chart
  const ProfitMarginTooltip = ({ active, label, payload, title }) => {
    if (!active || !payload?.length) return null

    const profitMarginEntry = payload.find((entry) => entry?.dataKey === 'profitMargin')
    const rawValue = profitMarginEntry?.value
    const numericValue = Number(rawValue)

    const formattedValue = Number.isFinite(numericValue)
      ? `${numericValue.toFixed(1)}%`
      : '—'

    return (
      <div className="bg-white shadow-md rounded-md px-4 py-3">
        <div className="text-black mb-1">{label}</div>
      <div className="font-medium text-[#E1174C]">{title} {formattedValue}</div>
      </div>
    )
  }

  // Memoize expensive calculations
  const expenseBreakdownData = useMemo(() => {
    if (!currentMetrics) return []
    return [
      {
        name: t('charts.legends.operating'),
        value: currentMetrics.rawData.expenses * 0.6,
        percentage: 60,
        color: EXPENSE_BREAKDOWN_COLORS.operating
      },
      {
        name: t('charts.legends.marketing'),
        value: currentMetrics.rawData.expenses * 0.25,
        percentage: 25,
        color: EXPENSE_BREAKDOWN_COLORS.marketing
      },
      {
        name: t('charts.legends.technology'),
        value: currentMetrics.rawData.expenses * 0.15,
        percentage: 15,
        color: EXPENSE_BREAKDOWN_COLORS.technology
      }
    ]
  }, [currentMetrics, t])

  const renderExpenseBreakdownLabel = ({
    cx,
    cy,
    midAngle,
    outerRadius,
    percent,
    name,
    payload,
    fill
  }) => {
    const displayName = payload?.name ?? name ?? ''
    const radius = outerRadius + 26
    const x = cx + radius * Math.cos(-midAngle * RADIAN)
    const rawY = cy + radius * Math.sin(-midAngle * RADIAN)
    const y = Math.max(rawY, 18) // prevent top-edge clipping
    const textAnchor = x > cx ? 'start' : 'end'

    return (
      <text
        x={x}
        y={y}
        fill={fill || payload?.color || COLORS[0]}
        textAnchor={textAnchor}
        dominantBaseline="central"
        style={{ fontSize: 16, fontWeight: 500 }}
      >
        {`${displayName} ${(percent * 100).toFixed(0)}%`}
      </text>
    )
  }

  // Memoize processed historical data for charts
  const chartData = useMemo(() => {
    return historicalDataThroughSelectedMonth.slice(-6) // Last 6 months through the selected month.
  }, [historicalDataThroughSelectedMonth])

  const financialTrendData = historicalDataThroughSelectedMonth

  const missingTrendMonths = useMemo(() => {
    if (financialTrendData.length < 2) return []

    const recordedMonths = new Set(
      financialTrendData.map((row) => getMetricMonthKey({ year: row.year, month: row.monthNumber }))
    )
    const firstMonthIndex = getMonthIndex(
      financialTrendData[0].year,
      financialTrendData[0].monthNumber
    )
    const lastMonthIndex = getMonthIndex(
      financialTrendData[financialTrendData.length - 1].year,
      financialTrendData[financialTrendData.length - 1].monthNumber
    )
    const missingMonths = []

    for (let monthIndex = firstMonthIndex + 1; monthIndex < lastMonthIndex; monthIndex += 1) {
      const year = Math.floor(monthIndex / 12)
      const month = (monthIndex % 12) + 1
      if (!recordedMonths.has(getMetricMonthKey({ year, month }))) {
        missingMonths.push(getMonthName(month, year))
      }
    }

    return missingMonths
  }, [financialTrendData, getMonthName])

  const showConversionRateZeroLeadsHintInTable = chartData.some(
    (row) => Number(row?.leads || 0) <= 0
  )

  // Column label for monthly performance table: singular vs plural
  const monthColumnLabel =
    chartData.length === 1 ? t('charts.monthColumn.single') : t('charts.monthColumn.plural')

  const reports = [
    { key: 'revenue', title: t('charts.revenueTitle') },
    { key: 'profit', title: t('charts.profitTitle') },
    { key: 'performance', title: t('charts.performanceTitle') },
    { key: 'expense', title: t('charts.expenseTitle') },
    { key: 'monthlyPerformance', title: t('charts.monthlyPerformanceTitle') }
  ]

  const safeReportIndex = Math.min(Math.max(activeReportIndex, 0), reports.length - 1)
  const activeReport = reports[safeReportIndex]
  const isFirstTimeCustomer = hasAnyMonthlyMetrics === false

  const renderBrandedChartTitle = useCallback(
    (titleKey) => (
      <>
        <span>{t(`charts.titleParts.${titleKey}.prefix`)}</span>
        <span className="text-[#FF1B54]">{t(`charts.titleParts.${titleKey}.accent`)}</span>
        <span>{t(`charts.titleParts.${titleKey}.suffix`)}</span>
      </>
    ),
    [t]
  )

  const handleExportMonthlyPerformance = useCallback(() => {
    const headers = [
      monthColumnLabel,
      t('charts.legends.revenue'),
      t('charts.legends.expenses'),
      t('kpi.netProfit.title'),
      t('kpi.profitMargin.title'),
      t('kpi.conversionRate.title')
    ]
    const csvCell = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`
    const rows = chartData.map((row) => [
      row.month,
      row.revenue,
      row.expenses,
      row.netProfit,
      `${row.profitMargin.toFixed(1)}%`,
      `${row.conversionRate.toFixed(1)}%`
    ])
    const csv = [headers, ...rows].map((row) => row.map(csvCell).join(',')).join('\n')
    const url = URL.createObjectURL(new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' }))
    const link = document.createElement('a')
    link.href = url
    link.download = 'monthly-performance.csv'
    document.body.appendChild(link)
    link.click()
    link.remove()
    URL.revokeObjectURL(url)
  }, [chartData, monthColumnLabel, t])

  const renderDashboardEmptyState = ({ title, message }) => (
    <div className="bg-white rounded-lg shadow dashboard-card border-gray-200 ring-gray-100/80">
      <div className="flex min-h-[400px] items-start justify-center px-6 pt-24 pb-20 sm:px-8 sm:pt-28 sm:pb-24">
        <div className="max-w-4xl text-center">
          <div className="mb-6 flex justify-center text-gray-400">
            <BarChart3
              className="h-14 w-14 stroke-[1.75]"
              role="img"
              aria-label={t('charts.firstTimeWelcomeIconLabel')}
            />
          </div>
          <h3 className="text-3xl font-semibold tracking-tight text-gray-600">{title}</h3>
          <p className="mt-6 text-lg md:text-xl leading-relaxed text-gray-500">{message}</p>
        </div>
      </div>
    </div>
  )

  const renderFirstTimeOnboardingState = () =>
    renderDashboardEmptyState({
      title: t('charts.firstTimeWelcomeTitle'),
      message: t('charts.firstTimeWelcomeMessage'),
    })

  const renderGenericNoDataState = () =>
    renderDashboardEmptyState({
      title: t('charts.noDataMessage'),
      message: t('dataEntry.subtitle', { month: 'current', year: new Date().getFullYear() }),
    })

  const renderNoDataState = () =>
    isFirstTimeCustomer ? renderFirstTimeOnboardingState() : renderGenericNoDataState()

  const renderActiveReport = () => {
    if (loading) {
      return (
        <div className="flex justify-center items-center h-64">
          <div role="status" aria-label={t('customer.loadingDashboard')} className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      )
    }

    if (error) {
      return (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">{error}</p>
        </div>
      )
    }

    if (!currentMetrics || historicalDataThroughSelectedMonth.length === 0) {
      return renderNoDataState()
    }

    if (activeReport?.key === 'revenue') {
      return (
        <div className="bg-white rounded-lg p-6 dashboard-card shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900">{renderBrandedChartTitle('revenue')}</h3>
          <p className="mt-1 mb-4 text-sm text-gray-600">{t('charts.revenueSubtitle')}</p>
          <div className="h-[340px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={historicalDataThroughSelectedMonth} barCategoryGap="40%">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="shortMonth" />
                <YAxis />
                <Tooltip
                  formatter={(value, _name, props) => [
                    `$${value.toLocaleString()}`,
                    props.dataKey === 'revenue'
                      ? t('charts.legends.revenue')
                      : t('charts.legends.expenses')
                  ]}
                />
                <Legend />
                <Bar
                  dataKey="revenue"
                  fill="#10B981"
                  barSize={22}
                  name={t('charts.legends.revenue')}
                />
                <Bar
                  dataKey="expenses"
                  fill="#EF4444"
                  barSize={22}
                  name={t('charts.legends.expenses')}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )
    }

    if (activeReport?.key === 'profit') {
      return (
        <div className="bg-white rounded-lg p-6 dashboard-card shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">{renderBrandedChartTitle('profit')}</h3>
          <div className="h-[340px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={historicalDataThroughSelectedMonth} barCategoryGap="40%">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="shortMonth" />
                <YAxis />
                <Tooltip
                  content={<ProfitMarginTooltip title={t('kpi.profitMargin.title')} />}
                />
                <Legend />
                <Bar
                  dataKey="profitMargin"
                  fill="#10B981"
                  barSize={22}
                  name={`${t('kpi.profitMargin.title')} ${currentMetrics?.profitMargin?.toFixed(1) ?? ''}%`}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )
    }

    if (activeReport?.key === 'performance') {
      return (
        <div className="bg-white rounded-lg p-6 dashboard-card shadow-sm border-[#C63D67] ring-[#C63D67]/20">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">{renderBrandedChartTitle('performance')}</h3>
          <div className="h-[340px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} barCategoryGap="40%">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="shortMonth" />
                <YAxis />
                <Tooltip
                  formatter={(value, _name, props) => {
                    if (props.dataKey === 'conversionRate' || props.dataKey === 'profitMargin') {
                      return [
                        `${value.toFixed(1)}%`,
                        props.dataKey === 'conversionRate'
                          ? t('kpi.conversionRate.title')
                          : t('kpi.profitMargin.title')
                      ]
                    }
                    return [
                      value.toLocaleString(),
                      props.dataKey === 'leads'
                        ? t('charts.legends.leads')
                        : t('charts.legends.customers')
                    ]
                  }}
                />
                <Legend />
                <Bar
                  dataKey="leads"
                  fill={EXPENSE_BREAKDOWN_COLORS.technology}
                  barSize={22}
                  name={t('charts.legends.leads')}
                />
                <Bar
                  dataKey="customers"
                  fill="#10B981"
                  barSize={22}
                  name={t('charts.legends.customers')}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )
    }

    if (activeReport?.key === 'monthlyPerformance') {
      return (
        <div className="bg-white rounded-lg p-6 dashboard-card shadow-sm border-[#C63D67] ring-[#C63D67]/20">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">{renderBrandedChartTitle('monthlyPerformance')}</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{monthColumnLabel}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('charts.legends.revenue')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('charts.legends.expenses')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('kpi.netProfit.title')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('kpi.profitMargin.title')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('kpi.conversionRate.title')}</th>
                  <th className="px-6 py-3" />
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {chartData.map((row, index) => (
                  <tr key={row.id || index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{row.month}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">${row.revenue.toLocaleString()}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">${row.expenses.toLocaleString()}</td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${row.netProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      ${row.netProfit.toLocaleString()}
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm ${row.profitMargin >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {row.profitMargin.toFixed(1)}%
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row.conversionRate.toFixed(1)}%</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                      <button
                        type="button"
                        onClick={() => handleOpenDeleteMonth(row)}
                        disabled={isDeletingMonth}
                        className="inline-flex items-center justify-center text-gray-400 hover:text-red-600 disabled:opacity-40 disabled:cursor-not-allowed"
                        aria-label={t('charts.deleteMonthActionLabel', { month: row.month })}
                      >
                        <XIcon className="w-4 h-4" aria-hidden="true" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )
    }

    // expense breakdown
    return (
      <div className="bg-white rounded-lg p-6 dashboard-card shadow-sm border-[#C63D67] ring-[#C63D67]/20">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          {renderBrandedChartTitle('expense')}
        </h3>
        <div
          className="h-[420px] select-none expense-breakdown-chart"
          onMouseDownCapture={(event) => event.preventDefault()}
          onClickCapture={(event) => {
            event.preventDefault()
            event.stopPropagation()
            if (typeof event?.target?.blur === 'function') event.target.blur()
          }}
          onFocusCapture={(event) => {
            if (typeof event?.target?.blur === 'function') event.target.blur()
          }}
        >
          <ResponsiveContainer width="100%" height="100%">
            <PieChart
              rootTabIndex={-1}
              tabIndex={-1}
              onMouseDownCapture={(event) => event.preventDefault()}
              onClickCapture={(event) => {
                event.preventDefault()
                event.stopPropagation()
                if (typeof event?.target?.blur === 'function') event.target.blur()
              }}
              onFocusCapture={(event) => {
                if (typeof event?.target?.blur === 'function') event.target.blur()
              }}
              style={{ outline: 'none' }}
            >
              <Pie
                data={expenseBreakdownData}
                cx="50%"
                cy="45%"
                rootTabIndex={-1}
                labelLine={false}
                label={renderExpenseBreakdownLabel}
                outerRadius={120}
                fill="#8884d8"
                dataKey="value"
                style={{ outline: 'none' }}
              >
                {expenseBreakdownData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} tabIndex={-1} style={{ outline: 'none' }} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => [`$${value.toLocaleString()}`, t('charts.amountLabel')]} />
              <Legend
                verticalAlign="bottom"
                align="center"
                iconType="square"
                formatter={(value, entry) => (
                  <span
                    style={{
                      color:
                        entry?.color || entry?.payload?.color || entry?.payload?.fill || '#111827',
                      fontWeight: 500
                    }}
                  >
                    {value}
                  </span>
                )}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
    )
  }

  const dashboardContent = () => {
    if (loading) {
      return (
        <div className="flex justify-center items-center h-64">
          <div role="status" aria-label={t('customer.loadingDashboard')} className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      )
    }

    if (error) {
      return (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">{error}</p>
        </div>
      )
    }

    if (!currentMetrics || historicalDataThroughSelectedMonth.length === 0) {
      return (
        <div className="space-y-6">
          {renderNoDataState()}

          {automationProfile?.is_enabled && (
            <AutomationsCard businessId={businessId} />
          )}
        </div>
      )
    }

    return (
      <div className="space-y-6">
        {/* Business Intelligence Section */}
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-[300px_minmax(0,1fr)]">
          {/* Health Score Card */}
          <div>
            <HealthScoreCard 
              currentMetrics={currentMetrics}
              historicalMetrics={historicalDataThroughSelectedMonth}
              businessName={businessName}
            />
          </div>
          
          {/* Insights Panel */}
          <div>
            <InsightsPanel 
              currentMetrics={currentMetrics}
              historicalData={historicalDataThroughSelectedMonth}
              hasAutomationConfigured={hasConfiguredAutomation}
            />
          </div>

        </div>

        {/* KPI Cards */}
        <section className="flex flex-col gap-[14px]" aria-labelledby="performance-overview-title">
          <div className="flex flex-wrap items-end justify-between gap-x-4 gap-y-1">
            <div className="flex flex-col gap-1">
              <h2 id="performance-overview-title" className="text-xl font-bold leading-6 tracking-tight text-[#111827]">
                {t('kpi.overviewTitle')}
              </h2>
              <p className="text-[13px] leading-4 text-[#667085]">
                {comparisonOverviewSubtitle}
              </p>
            </div>
            {updatedAgoLabel && (
              <time
                dateTime={new Date(lastDashboardUpdateAt).toISOString()}
                className="ml-auto shrink-0 text-xs leading-4 text-[#667085]"
              >
                {updatedAgoLabel}
              </time>
            )}
          </div>

          <div className={`grid grid-cols-1 items-stretch gap-[14px] sm:grid-cols-2 lg:grid-cols-3 ${kpiGridXlClass}`}>
          <KPICard
            metricType="revenue"
            title={t('kpi.revenue.title')}
            value={currentMetrics?.rawData?.revenue}
            previousValue={previousMetrics?.rawData?.revenue}
            comparisonLabel={comparisonTrendLabel}
            comparisonOptions={comparisonMonthOptions}
            selectedComparisonKey={selectedComparisonOption?.key || null}
            onComparisonMonthSelect={
              hasComparisonMonthSelector ? setSelectedComparisonMonthKey : null
            }
            isCurrency={true}
          />

          <KPICard
            metricType="netProfit"
            title={t('kpi.netProfit.title')}
            value={currentMetrics.netProfit}
            previousValue={previousMetrics?.netProfit}
            comparisonLabel={comparisonTrendLabel}
            comparisonOptions={comparisonMonthOptions}
            selectedComparisonKey={selectedComparisonOption?.key || null}
            onComparisonMonthSelect={
              hasComparisonMonthSelector ? setSelectedComparisonMonthKey : null
            }
            isCurrency={true}
          />

          <KPICard
            metricType="profitMargin"
            title={t('kpi.profitMargin.title')}
            value={currentMetrics.profitMargin}
            previousValue={previousMetrics?.profitMargin}
            comparisonLabel={comparisonTrendLabel}
            comparisonOptions={comparisonMonthOptions}
            selectedComparisonKey={selectedComparisonOption?.key || null}
            onComparisonMonthSelect={
              hasComparisonMonthSelector ? setSelectedComparisonMonthKey : null
            }
            isPercentage={true}
          />

          <KPICard
            metricType="expenseRatio"
            title={t('kpi.expenseRatio.title')}
            value={currentMetrics.expenseRatio}
            previousValue={previousMetrics?.expenseRatio}
            comparisonLabel={comparisonTrendLabel}
            comparisonOptions={comparisonMonthOptions}
            selectedComparisonKey={selectedComparisonOption?.key || null}
            onComparisonMonthSelect={
              hasComparisonMonthSelector ? setSelectedComparisonMonthKey : null
            }
            isPercentage={true}
          />

          {hasRevenueComparison && (
            <KPICard
              metricType="revenueGrowth"
              title={t('kpi.revenueGrowth.title')}
              value={currentMetrics.revenueGrowth}
              previousValue={null} // Growth is already a comparison
              comparisonLabel={comparisonTrendLabel}
              comparisonOptions={comparisonMonthOptions}
              selectedComparisonKey={selectedComparisonOption?.key || null}
              onComparisonMonthSelect={
                hasComparisonMonthSelector ? setSelectedComparisonMonthKey : null
              }
              isPercentage={true}
            />
          )}

          {hasUsableLeadsInput && (
            <KPICard
              metricType="conversionRate"
              title={t('kpi.conversionRate.title')}
              value={currentMetrics.conversionRate}
              previousValue={previousMetrics?.conversionRate}
              comparisonLabel={comparisonTrendLabel}
              comparisonOptions={comparisonMonthOptions}
              selectedComparisonKey={selectedComparisonOption?.key || null}
              onComparisonMonthSelect={
                hasComparisonMonthSelector ? setSelectedComparisonMonthKey : null
              }
              isPercentage={true}
            />
          )}
          </div>
        </section>

        {/* Charts Section */}
        {historicalDataThroughSelectedMonth.length > 0 && (
          <div className="space-y-5">
            <div className="grid grid-cols-1 gap-5 xl:grid-cols-[1.5fr_1fr]">
              <section className="flex min-w-0 flex-col gap-3.5 rounded-[14px] border border-[#E7E9EE] bg-white p-5 sm:p-6" aria-labelledby="revenue-expenses-chart-title">
                <header>
                  <h3 id="revenue-expenses-chart-title" className="text-base font-bold text-[#111827]">
                    {t('charts.revenueExpensesTitle')}
                  </h3>
                  <p className="mt-1 text-xs text-[#667085]">{t('charts.monthlyTotalsUsd')}</p>
                </header>
                <ResponsiveContainer width="100%" height={230}>
                  <BarChart data={financialTrendData} barCategoryGap="34%" margin={{ top: 4, right: 4, left: 2, bottom: 0 }}>
                    <CartesianGrid vertical={false} stroke="#EAECF0" />
                    <XAxis dataKey="chartMonth" axisLine={{ stroke: '#D0D5DD' }} tickLine={false} tick={{ fill: '#475467', fontSize: 10 }} />
                    <YAxis
                      width={52}
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: '#667085', fontSize: 10 }}
                      tickFormatter={(value) => new Intl.NumberFormat(currentLanguage, { style: 'currency', currency: 'USD', notation: 'compact', maximumFractionDigits: 0 }).format(value)}
                    />
                    <Tooltip
                      formatter={(value, name) => [
                        new Intl.NumberFormat(currentLanguage, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(Number(value)),
                        name,
                      ]}
                      labelFormatter={(_label, payload) => payload?.[0]?.payload?.month || _label}
                      contentStyle={{ border: '1px solid #E7E9EE', borderRadius: 10, boxShadow: '0 8px 24px rgba(16, 24, 40, 0.08)' }}
                    />
                    <Bar dataKey="revenue" fill="#FF1B54" barSize={24} radius={[4, 4, 0, 0]} name={t('charts.legends.revenue')} />
                    <Bar dataKey="expenses" fill="#F7A3B7" barSize={18} radius={[4, 4, 0, 0]} name={t('charts.legends.expenses')} />
                  </BarChart>
                </ResponsiveContainer>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[#475467]">
                  <span className="inline-flex items-center gap-1.5 text-xs">
                    <span className="h-2 w-2 rounded-sm bg-[#FF1B54]" aria-hidden="true" />
                    {t('charts.legends.revenue')}
                  </span>
                  <span className="inline-flex items-center gap-1.5 text-xs">
                    <span className="h-2 w-2 rounded-sm bg-[#F7A3B7]" aria-hidden="true" />
                    {t('charts.legends.expenses')}
                  </span>
                  {missingTrendMonths.length > 0 && (
                    <span className="text-[#98A2B3]">
                      {t('charts.noDataRecordedForMonths', { months: missingTrendMonths.join(', ') })}
                    </span>
                  )}
                </div>
              </section>

              <section className="flex min-w-0 flex-col gap-3 rounded-[14px] border border-[#E7E9EE] bg-white p-5 sm:p-6" aria-labelledby="profit-margin-chart-title">
                <header>
                  <h3 id="profit-margin-chart-title" className="text-base font-bold text-[#111827]">
                    {t('charts.profitMarginTitle')}
                  </h3>
                  <p className="mt-1 text-xs text-[#667085]">{t('charts.monthlyMarginTrend')}</p>
                </header>
                <ResponsiveContainer width="100%" height={185}>
                  <LineChart data={financialTrendData} margin={{ top: 8, right: 6, left: 0, bottom: 0 }}>
                    <CartesianGrid vertical={false} stroke="#EAECF0" />
                    <XAxis dataKey="chartMonth" axisLine={false} tickLine={false} tick={{ fill: '#475467', fontSize: 10 }} />
                    <YAxis
                      width={38}
                      domain={[0, 100]}
                      ticks={[0, 25, 50, 75, 100]}
                      tickFormatter={(value) => `${value}%`}
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: '#667085', fontSize: 10 }}
                    />
                    <Tooltip content={<ProfitMarginTooltip title={t('kpi.profitMargin.title')} />} />
                    <Line
                      type="monotone"
                      dataKey="profitMargin"
                      stroke="#FF1B54"
                      strokeWidth={3}
                      dot={{ r: 4, fill: '#FFFFFF', stroke: '#FF1B54', strokeWidth: 2.5 }}
                      activeDot={{ r: 5, fill: '#FFFFFF', stroke: '#FF1B54', strokeWidth: 3 }}
                      name={t('kpi.profitMargin.title')}
                    />
                  </LineChart>
                </ResponsiveContainer>
                <div className="flex items-center justify-between rounded-[9px] bg-[#FFF5F7] px-3 py-2.5">
                  <span className="text-xs text-[#667085]">{t('charts.currentMargin')}</span>
                  <span className="text-[15px] font-bold text-[#E1174C]">{currentMetrics.profitMargin.toFixed(1)}%</span>
                </div>
              </section>
            </div>

            <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
              <section className="flex min-w-0 flex-col gap-3 rounded-[14px] border border-[#E7E9EE] bg-white p-5 sm:p-6" aria-labelledby="expense-breakdown-chart-title">
                <header>
                  <h3 id="expense-breakdown-chart-title" className="text-base font-bold text-[#111827]">
                    {t('charts.expenseTitle')}
                  </h3>
                  <p className="mt-1 text-xs text-[#667085]">{t('charts.expenseSubtitle')}</p>
                </header>
                <div className="flex flex-1 flex-col items-center justify-center gap-6 py-2 sm:flex-row sm:gap-8">
                  <div className="relative h-[140px] w-[140px] shrink-0">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={expenseBreakdownData} dataKey="percentage" innerRadius={50} outerRadius={68} paddingAngle={2} stroke="#FFFFFF" strokeWidth={2}>
                          {expenseBreakdownData.map((entry) => (
                            <Cell key={entry.name} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(_value, name, item) => [new Intl.NumberFormat(currentLanguage, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(Number(item?.payload?.value || 0)), name]} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                      <span className="text-[19px] font-extrabold text-[#111827]">
                        {new Intl.NumberFormat(currentLanguage, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(Number(currentMetrics.rawData.expenses || 0))}
                      </span>
                      <span className="text-[10px] text-[#667085]">{t('charts.totalExpenses')}</span>
                    </div>
                  </div>
                  <div className="flex w-full flex-col gap-3.5 sm:w-auto">
                    {expenseBreakdownData.map((entry) => (
                      <div key={entry.name} className="flex items-center gap-2.5 text-xs">
                        <span className="h-2.5 w-2.5 shrink-0 rounded-[3px]" style={{ backgroundColor: entry.color }} aria-hidden="true" />
                        <span className="text-[#475467]">{entry.name}</span>
                        <span className="font-bold text-[#111827]">{entry.percentage}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              </section>

              <section className="flex min-w-0 flex-col gap-3 rounded-[14px] border border-[#E7E9EE] bg-white p-5 sm:p-6" aria-labelledby="customer-acquisition-title">
                <header className="flex items-start justify-between gap-3">
                  <div>
                    <h3 id="customer-acquisition-title" className="text-base font-bold text-[#111827]">
                      {t('charts.customerAcquisitionTitle')}
                    </h3>
                    <p className="mt-1 text-xs text-[#667085]">{t('charts.customerAcquisitionSubtitle')}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setCustomerAcquisitionSaveError('')
                      setCustomerAcquisitionModalOpen(true)
                    }}
                    className="shrink-0 rounded-md text-sm font-semibold text-[#E1174C] transition-colors hover:text-[#C91545] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF1B54]/30"
                  >
                    {t('charts.addDataLink')} →
                  </button>
                </header>
                <div className="relative flex flex-1 flex-col items-center justify-center gap-5 rounded-[11px] border border-dashed border-[#E4E7EC] bg-[#FAFAFB] p-5 pb-4 sm:flex-row sm:gap-6 sm:pb-12">
                  <div className="flex min-w-[90px] flex-col items-center gap-1">
                    <span className="text-[26px] font-extrabold leading-8 text-[#111827]">{Number(currentMetrics.rawData.leads || 0).toLocaleString(currentLanguage)}</span>
                    <span className="text-[11px] text-[#667085]">{t('charts.legends.leads')}</span>
                  </div>
                  <span className="hidden h-11 w-px shrink-0 bg-[#E4E7EC] sm:block" aria-hidden="true" />
                  <div className="flex min-w-[90px] flex-col items-center gap-1">
                    <span className="text-[26px] font-extrabold leading-8 text-[#111827]">{Number(currentMetrics.rawData.customers || 0).toLocaleString(currentLanguage)}</span>
                    <span className="text-[11px] text-[#667085]">{t('charts.legends.customers')}</span>
                  </div>
                  {Number(currentMetrics.rawData.customers || 0) === 0 && Number(currentMetrics.rawData.leads || 0) === 0 && (
                    <>
                      <span className="hidden h-11 w-px shrink-0 bg-[#E4E7EC] sm:block" aria-hidden="true" />
                      <div className="max-w-[190px] text-center">
                        <p className="text-[13px] font-semibold text-[#111827]">{t('charts.noRecordsYet')}</p>
                        <p className="mt-1 text-[11px] text-[#667085]">{t('charts.importCustomerDataHint')}</p>
                      </div>
                    </>
                  )}
                  <div className="flex w-full shrink-0 items-center justify-end gap-1.5 whitespace-nowrap sm:absolute sm:bottom-4 sm:right-5 sm:w-auto">
                    <span className="text-sm font-semibold text-[#475467]">{t('kpi.conversionRate.title')}:</span>
                    <span className="text-sm font-bold text-[#E1174C]">
                      {new Intl.NumberFormat(currentLanguage, { maximumFractionDigits: 0 }).format(currentMetrics.conversionRate)}%
                    </span>
                  </div>
                </div>
                <button
                  type="button"
                  aria-haspopup="dialog"
                  aria-controls={customerAcquisitionInfoModalOpen ? 'customer-acquisition-info-dialog' : undefined}
                  aria-expanded={customerAcquisitionInfoModalOpen}
                  onClick={() => setCustomerAcquisitionInfoModalOpen(true)}
                  className="w-fit cursor-pointer rounded-sm text-left text-[13px] font-bold leading-5 text-[#111827] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF1B54]/30"
                >
                  {t('charts.customerAcquisitionWhyItMatters')}{' '}
                  <span className="text-[#E1174C]">{t('charts.customerAcquisitionLearnMore')}</span>
                </button>
              </section>
            </div>
          </div>
        )}

        {/* Historical Data Table */}
        {historicalDataThroughSelectedMonth.length > 0 && (
          <div className="overflow-hidden rounded-2xl border border-[#E4E7EC] bg-white shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-4 border-b border-[#EAECF0] px-5 py-4 sm:px-6">
              <div>
                <h3 className="text-base font-semibold tracking-tight text-[#101828]">{t('charts.monthlyPerformanceTitle')}</h3>
                <p className="mt-1 text-xs text-[#667085]">{t('charts.monthlyPerformanceSubtitle')}</p>
              </div>
              <button
                type="button"
                onClick={handleExportMonthlyPerformance}
                className="inline-flex items-center gap-1.5 rounded-md px-2 py-1.5 text-sm font-semibold text-[#FF1B54] transition-colors hover:bg-[#FFF1F4] hover:text-[#E1174C] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF1B54]/30"
              >
                {t('charts.exportReport')}
                <ArrowDownToLine className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-[#EAECF0]">
                <thead className="bg-[#F9FAFB]">
                  <tr>
                    <th className="px-5 py-2.5 text-left text-[10px] font-semibold uppercase tracking-[0.08em] text-[#667085]">{monthColumnLabel}</th>
                    <th className="px-5 py-2.5 text-left text-[10px] font-semibold uppercase tracking-[0.08em] text-[#667085]">{t('charts.legends.revenue')}</th>
                    <th className="px-5 py-2.5 text-left text-[10px] font-semibold uppercase tracking-[0.08em] text-[#667085]">{t('charts.legends.expenses')}</th>
                    <th className="px-5 py-2.5 text-left text-[10px] font-semibold uppercase tracking-[0.08em] text-[#667085]">{t('kpi.netProfit.title')}</th>
                    <th className="px-5 py-2.5 text-left text-[10px] font-semibold uppercase tracking-[0.08em] text-[#667085]">{t('kpi.profitMargin.title')}</th>
                    <th
                      className={`relative px-5 py-2.5 text-left text-[10px] font-semibold uppercase tracking-[0.08em] text-[#667085] group ${
                        showConversionRateZeroLeadsHintInTable ? 'cursor-help' : ''
                      }`}
                      title={
                        showConversionRateZeroLeadsHintInTable
                          ? t('kpi.conversionRate.zeroLeadsHint')
                          : undefined
                      }
                    >
                      {t('kpi.conversionRate.title')}

                      {showConversionRateZeroLeadsHintInTable && (
                        <div className="pointer-events-none absolute left-1/2 top-full -translate-x-1/2 translate-y-2 opacity-0 group-hover:opacity-100 transition-opacity duration-150 z-50 normal-case">
                          <div className="mx-auto mb-[-4px] h-2 w-2 rotate-45 bg-gray-900" />
                          <div className="max-w-xs rounded-md bg-gray-900 text-white text-xs px-3 py-2 shadow-lg">
                            {t('kpi.conversionRate.zeroLeadsHint')}
                          </div>
                        </div>
                      )}
                    </th>
                    <th className="px-4 py-2.5" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#EAECF0] bg-white">
                  {chartData.map((row, index) => (
                    <tr key={row.id || index} className="transition-colors hover:bg-[#FAFBFC]">
                      <td className="whitespace-nowrap px-5 py-3 text-xs font-semibold text-[#101828]">{row.month}</td>
                      <td className="whitespace-nowrap px-5 py-3 text-xs text-[#101828]">${row.revenue.toLocaleString()}</td>
                      <td className="whitespace-nowrap px-5 py-3 text-xs text-[#101828]">${row.expenses.toLocaleString()}</td>
                      <td className={`whitespace-nowrap px-5 py-3 text-xs font-semibold ${row.netProfit >= 0 ? 'text-[#078343]' : 'text-red-600'}`}>
                        ${row.netProfit.toLocaleString()}
                      </td>
                      <td className={`whitespace-nowrap px-5 py-3 text-xs font-semibold ${row.profitMargin >= 0 ? 'text-[#078343]' : 'text-red-600'}`}>
                        {row.profitMargin.toFixed(1)}%
                      </td>
                      <td className="whitespace-nowrap px-5 py-3 text-xs text-[#667085]">
                        <div className="relative inline-flex items-center group">
                          <span title={
                            Number(row.leads || 0) <= 0
                              ? t('kpi.conversionRate.zeroLeadsHint')
                              : undefined
                          }>
                            {row.conversionRate.toFixed(1)}%
                          </span>

                          {Number(row.leads || 0) <= 0 && (
                            <div className="pointer-events-none absolute left-1/2 top-0 -translate-x-1/2 -translate-y-[calc(100%+10px)] opacity-0 group-hover:opacity-100 transition-opacity duration-150 z-20">
                              <div className="max-w-xs rounded-md bg-gray-900 text-white text-xs px-3 py-2 shadow-lg">
                                {t('kpi.conversionRate.zeroLeadsHint')}
                              </div>
                              <div className="mx-auto mt-[-4px] h-2 w-2 rotate-45 bg-gray-900" />
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-right">
                        <button
                          type="button"
                          onClick={() => handleOpenDeleteMonth(row)}
                          disabled={isDeletingMonth}
                          className="inline-flex items-center justify-center text-gray-400 hover:text-red-600 disabled:opacity-40 disabled:cursor-not-allowed"
                          aria-label={t('charts.deleteMonthActionLabel', { month: row.month })}
                        >
                          <XIcon className="w-4 h-4" aria-hidden="true" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {automationProfile?.is_enabled && (
          <AutomationsCard businessId={businessId} />
        )}
      </div>
    )
  }

  return (
    <>
      <ReportsModal
        isOpen={reportsOpen}
        title={t('customer.reportsModalTitle')}
        onClose={onCloseReports}
        onPrev={() => setActiveReportIndex((i) => Math.max(0, i - 1))}
        onNext={() => setActiveReportIndex((i) => Math.min(reports.length - 1, i + 1))}
        prevDisabled={safeReportIndex === 0}
        nextDisabled={safeReportIndex === reports.length - 1}
        prevLabel={t('customer.reportsPrev')}
        nextLabel={t('customer.reportsNext')}
        closeLabel={t('customer.reportsClose')}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="text-sm text-gray-700 font-medium">{activeReport?.title}</div>
          <div className="text-sm text-gray-500">
            {t('customer.reportsCount', { current: safeReportIndex + 1, total: reports.length })}
          </div>
        </div>
        {renderActiveReport()}
      </ReportsModal>

      <div className="space-y-6">
        <DashboardOverviewHeader
          businessName={businessName}
          selectedMonthKey={selectedDashboardMonthKey || currentMonthKey}
          monthOptions={dashboardMonthOptions}
          onMonthChange={setSelectedDashboardMonthKey}
          onDeleteData={onDeleteData}
        />
        {dashboardContent()}
      </div>

      <CustomerAcquisitionModal
        isOpen={customerAcquisitionModalOpen}
        monthLabel={acquisitionMonthLabel}
        onClose={handleCloseCustomerAcquisitionModal}
        onSave={handleSaveCustomerAcquisition}
        saving={isSavingCustomerAcquisition}
        saveError={customerAcquisitionSaveError}
      />

      <CustomerAcquisitionInfoModal
        isOpen={customerAcquisitionInfoModalOpen}
        onClose={handleCloseCustomerAcquisitionInfoModal}
      />

      {deleteMonthOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) handleCancelDeleteMonth()
          }}
          role="dialog"
          aria-modal="true"
          aria-label={t('charts.deleteMonthConfirmTitle')}
        >
          <div className="bg-white rounded-lg p-6 w-full max-w-md border border-emerald-200">
            <h2 className="text-xl font-bold text-gray-900 mb-2">
              {t('charts.deleteMonthConfirmTitle')}
            </h2>
            <p className="text-sm text-gray-700 mb-4">
              {t('charts.deleteMonthConfirmBody', { month: selectedDeleteRow?.month || '' })}
            </p>

            {deleteMonthError && (
              <div className="mb-3 text-sm text-red-600">
                {deleteMonthError}
              </div>
            )}

            <div className="flex justify-end space-x-3 mt-2">
              <button
                type="button"
                onClick={handleCancelDeleteMonth}
                disabled={isDeletingMonth}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
              >
                {t('charts.deleteMonthConfirmNo')}
              </button>
              <button
                type="button"
                onClick={handleConfirmDeleteMonth}
                disabled={isDeletingMonth}
                className="px-4 py-2 text-sm font-medium text-white bg-[#FF1B54] border border-transparent rounded-md hover:bg-[#E1174C] disabled:opacity-50"
              >
                {isDeletingMonth ? 'Deleting…' : t('charts.deleteMonthConfirmYes')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default FinancialDashboard
